<template>
  <section class="faq" ref="rootEl">
    <nav v-if="hasNavigation" class="faq__nav">
      <div
        v-for="(category, i) in categories"
        :key="`category-${i}`"
        v-html="category"
        :class="generateCategoryClasses(category)"
        @click="makeActiveCategory(category)"
      ></div>
    </nav>
    <transition name="accordion-fade-slide" mode="out-in">
      <div v-if="showAccordion" class="accordion">
        <div
          class="accordion__item"
          v-for="(item, i) in categoryItems"
          :key="`accordion-item-${i}`"
        >
          <div :class="generateQuestionClasses(i)" @click="makeActive(i)">
            <p class="accordion__title-text" v-html="item[questionProperty]"></p>
            <BaseIcon class="accordion__icon" name="expand_more" size="24"></BaseIcon>
          </div>
          <collapse-transition>
            <div class="accordion__wrapper" v-if="i === activeQuestionIndex">
              <div class="accordion__padding">
                <div class="accordion__value" v-html="item[answerProperty]"></div>
                <a
                  v-if="item[answerLinktext]"
                  :href="item[answerLink]"
                  class="accordion__value"
                  v-html="item[answerLinktext]"
                ></a>
              </div>
            </div>
          </collapse-transition>
        </div>
      </div>
    </transition>
  </section>
</template>

<script>
import { CollapseTransition } from 'vue2-transitions'

export default {
  name: 'TextAccordion',
  components: {
    CollapseTransition
  },
  data() {
    return {
      activeTab: null,
      activeQuestionIndex: null,
      showAccordion: true
    }
  },
  props: {
    items: {
      type: Array,
      required: true
    },
    questionProperty: {
      type: String,
      default: 'title'
    },
    answerProperty: {
      type: String,
      default: 'value'
    },
    answerLink: {
      type: String,
      default: 'link'
    },
    answerLinktext: {
      type: String,
      default: 'linktext'
    },
    tabName: {
      type: String,
      default: 'category'
    }
  },
  created() {
    this.setFirstTabActive()
  },
  computed: {
    categories() {
      const uniqueCategories = this.items
        .map(item => item[this.tabName])
        .filter(
          (category, index, categories) =>
            categories.indexOf(category) === index
        )
      return uniqueCategories
    },
    categoryItems() {
      return this.items.filter(item => item[this.tabName] === this.activeTab)
    },
    hasNavigation() {
      return !!this.categories[0]
    }
  },
  methods: {
    setFirstTabActive() {
      this.activeTab = this.categories[0]
    },
    makeActive(itemIndex) {
      this.activeQuestionIndex === itemIndex
        ? (this.activeQuestionIndex = null)
        : (this.activeQuestionIndex = itemIndex)
    },
    generateButtonClasses(buttonIndex) {
      return [
        'accordion__toggle-button',
        this.activeQuestionIndex === buttonIndex
          ? 'accordion__toggle-button_active'
          : null
      ]
    },
    generateQuestionClasses(questionIndex) {
      return [
        'accordion__title',
        this.activeQuestionIndex === questionIndex
          ? 'accordion__title_active'
          : null
      ]
    },
    makeActiveCategory(category) {
      if (this.activeTab === category) return

      this.showAccordion = false
      this.activeTab = category
      this.activeQuestionIndex = null
      setTimeout(() => {
        this.showAccordion = true
      }, 300)
    },
    generateCategoryClasses(category) {
      return [
        'faq__nav-item',
        this.activeTab === category ? 'faq__nav-item_active' : null
      ]
    }
  }
}
</script>

<style lang="postcss">
.faq {
  @apply w-full mb-8;
}
.faq__nav {
  @apply w-full border border-t-0 border-l-0 border-r-0 border-body-lighter relative flex mb-8 overflow-x-scroll scrolling-touch;
}
.faq__nav-item {
  @apply text-black text-lg font-semibold px-2 py-4 mr-2 cursor-pointer whitespace-no-wrap;
}
.faq__nav-item:hover {
  @apply text-secondary;
}
.faq__nav-item.faq__nav-item_active {
  @apply border-4 border-t-0 border-l-0 border-r-0 border-secondary text-secondary;
}
.accordion {
  @apply bg-secondary-lightest;
}
.accordion-fade-slide-enter-active,
.accordion-fade-slide-leave-active {
  transition: all 0.3s;
}
.accordion-fade-slide-enter {
  @apply opacity-0;
}
.accordion-fade-slide-leave-to {
  @apply opacity-0;
}
.accordion__item {
  @apply w-full border-b border-white;
}
.accordion__item:last-child {
  @apply border-0;
}
.accordion__title {
  @apply flex justify-between items-center cursor-pointer p-6 text-secondary;
  transition: all 0.3s;
}
.accordion__title-text {
  @apply font-semibold text-lg text-primary;
}
.accordion__title:hover .accordion__title-text {
  @apply text-secondary;
}
.accordion__wrapper {
  @apply px-6;
}
.accordion__padding {
  @apply pb-6;
}
.accordion__value {
  @apply block w-full text-lg font-light text-body;
}
.accordion__value a {
  @apply text-secondary font-semibold no-underline cursor-pointer;
}
.accordion__value p {
  @apply mb-4;
}
.accordion__title_active .accordion__icon {
  transform: rotate(180deg);
  transition: all 0.3s;
}
@screen md {
  .faq {
    @apply mb-16;
  }
  .faq__nav {
    @apply mb-12;
  }
  .faq__nav-item {
    @apply mr-4;
  }
}
</style>